import { useState, useCallback, useEffect } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import AutomationService from "../../../../services/automation_service";
import IntegrationService from "../../../../services/integration_service";
import { AUTOMATION_TRIGGERS, MESSAGE_TEMPLATES } from "../../../../models/automation";

/**
 * useAutomations
 * Carrega as automações salvas e mescla com os triggers pré-definidos.
 * Todo trigger é exibido — os salvos vêm com dados, os não salvos vêm desativados.
 */
export const useAutomations = () => {
    const { idTenant, loading: tenantLoading, user } = useTenant();

    const [savedMap, setSavedMap] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isIntegrated, setIsIntegrated] = useState(false);

    // Carregar automações salvas do Firestore
    const loadData = useCallback(async () => {
        if (!idTenant) return;
        setIsLoading(true);
        try {
            // Verificar integração Evolution API
            const evolution = await IntegrationService.getByProvider(idTenant, "evolution");
            setIsIntegrated(!!(evolution?.isActive && evolution?.config?.apiUrl && evolution?.config?.apiKey && evolution?.config?.instanceName));

            const data = await AutomationService.getAll(idTenant);
            // Mapear por trigger ID para merge rápido
            const map = {};
            data.forEach(a => { map[a.trigger] = a; });
            setSavedMap(map);
        } catch (error) {
            console.error("Erro ao carregar automações:", error);
            toast.error("Erro ao carregar automações.");
        } finally {
            setIsLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        if (!tenantLoading && idTenant) loadData();
    }, [tenantLoading, idTenant, loadData]);

    /**
     * Mesclar triggers fixos com dados salvos.
     * Resultado: todos os triggers, com dados do Firestore se existirem.
     */
    const mergedTriggers = AUTOMATION_TRIGGERS.map(trigger => {
        const saved = savedMap[trigger.id];
        const defaultMessage = MESSAGE_TEMPLATES[trigger.id] || "";
        return {
            ...trigger,
            // Dados salvos
            docId: saved?.id || null,
            active: saved?.active || false,
            name: saved?.name || trigger.label,
            channel: saved?.channel || "whatsapp",
            message: saved?.message || defaultMessage,
            delayDays: saved?.delayDays || 0,
            scheduledTime: saved?.scheduledTime || "08:00",
        };
    });

    // Toggle ativar/desativar
    const handleToggleActive = useCallback(async (trigger) => {
        setIsSaving(true);
        try {
            const newActive = !trigger.active;
            if (trigger.docId) {
                // Já existe no Firestore — só toggle
                await AutomationService.toggleActive(idTenant, trigger.docId, newActive, user);
                setSavedMap(prev => ({
                    ...prev,
                    [trigger.id]: { ...prev[trigger.id], active: newActive }
                }));
            } else {
                // Não existe — criar com dados padrão
                const newData = {
                    trigger: trigger.id,
                    name: trigger.label,
                    channel: "whatsapp",
                    message: "",
                    delayDays: 0,
                    delayHours: 0,
                    active: true,
                };
                const saved = await AutomationService.save(newData, idTenant, user);
                setSavedMap(prev => ({
                    ...prev,
                    [trigger.id]: saved
                }));
            }
            toast.success(trigger.active ? "Automação desativada" : "Automação ativada");
        } catch (error) {
            toast.error("Erro ao alterar status.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user]);

    // Salvar edição (inline)
    const handleSave = useCallback(async (formData) => {
        setIsSaving(true);
        try {
            const dataToSave = {
                id: formData.docId || undefined,
                trigger: formData.triggerId,
                name: formData.name,
                channel: formData.channel,
                message: formData.message,
                delayDays: formData.delayDays,
                delayHours: formData.delayHours,
                scheduledTime: formData.scheduledTime,
                active: formData.active !== undefined ? formData.active : true,
            };
            const saved = await AutomationService.save(dataToSave, idTenant, user);
            setSavedMap(prev => ({
                ...prev,
                [formData.triggerId]: saved
            }));
            toast.success("Automação salva!");
        } catch (error) {
            console.error("Erro ao salvar automação:", error);
            toast.error("Erro ao salvar automação.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user]);

    return {
        mergedTriggers,
        isLoading,
        isSaving,
        handleToggleActive,
        handleSave,
        isIntegrated,
        isReady: !tenantLoading && !!idTenant,
    };
};
